import { API_BASE_URL } from '../config';

// --- Module Scope Variables ---
let authTokenProvider = async () => null; // Placeholder for token provider function

/**
 * Initializes the API module.
 * @param {object} options
 * @param {function(): Promise<string|null>} options.tokenProvider - Async function to get the auth token.
 */
export function initApi({ tokenProvider }) {
    if (tokenProvider) {
        authTokenProvider = tokenProvider;
        console.log('[API] Initialized with token provider.');
    } else {
         console.warn('[API] Initialized without a token provider. Authenticated requests will fail.');
    }
}

/**
 * 内部辅助函数，用于执行带有认证的 fetch 请求。
 * @param {string} endpoint API 路径 (e.g., '/api/subscriptions')
 * @param {RequestInit} options Fetch 选项 (method, headers, body, etc.)
 * @param {boolean} requiresAuth 是否需要认证 (默认为 true)
 * @returns {Promise<Response>} 返回原始的 Fetch Response 对象。
 * @throws {Error} 如果认证失败或网络请求失败则抛出错误。
 */
async function _fetchWithAuth(endpoint, options = {}, requiresAuth = true) {
    const headers = { ...(options.headers || {}) };
    let token = null;

    if (requiresAuth) {
        // Use the injected token provider
        token = await authTokenProvider();
        if (!token) {
            console.error(`[API] Authentication required for ${endpoint}, but no token available.`);
            throw new Error('认证令牌无效或丢失，请重新登录。');
        }
        headers['Authorization'] = `Bearer ${token}`;
    }

    // 自动添加 Content-Type for JSON if body is an object
    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(options.body); // Stringify body
    }

    const url = `${API_BASE_URL}${endpoint}`;
    console.log(`[API] Fetching: ${options.method || 'GET'} ${url}`);

    try {
        const response = await fetch(url, {
            ...options,
            headers,
        });

        // --- BEGIN MODIFICATION: Centralized 401 handling ---
        if (response.status === 401) {
             console.warn(`[API] Authentication failed (401) for ${url}. Triggering auth failure event.`);
             // Dispatch a custom event that other modules can listen for
             window.dispatchEvent(new CustomEvent('auth-failure', { detail: { endpoint } }));
             throw new Error('认证失败或会话已过期，请重新登录。'); // Throw specific error
        }
        // --- END MODIFICATION ---

        return response; // 返回原始响应，让调用者处理

    } catch (error) {
        console.error(`[API] Fetch error for ${url}:`, error);
        // 重新抛出错误，以便上层可以捕获
        // --- BEGIN MODIFICATION: Refined error re-throwing ---
        // Check if it's the specific auth error we threw above
        if (error.message.startsWith('认证失败')) {
             throw error; 
        } else if (error.message.startsWith('认证令牌无效')) {
             // This case might also trigger auth-failure if the provider returns null
             window.dispatchEvent(new CustomEvent('auth-failure', { detail: { endpoint, reason: 'no_token' } }));
             throw error;
        } else {
             throw new Error(`网络请求失败: ${error.message}`);
        }
        // --- END MODIFICATION ---
    }
}

/**
 * 处理 API 响应，检查是否成功并解析 JSON。
 * Also checks for 'login_required' status in non-401 responses.
 * @param {Response} response Fetch 响应对象。
 * @param {boolean} expectNoContent 对于 204 No Content 是否正常
 * @returns {Promise<any>} 解析后的 JSON 数据或在 204 时返回 null。
 * @throws {Error} 如果响应状态码表示失败或需要登录则抛出错误。
 */
async function _handleApiResponse(response, expectNoContent = false) {
    if (response.ok) {
        if (expectNoContent && response.status === 204) {
            return null; // No content expected and received
        }
        
        const text = await response.text();
        if (!text) {
            // Handle cases like 200 OK but empty body (if not expecting no content)
            if (!expectNoContent) { 
                console.warn(`[API] Received OK status (${response.status}) but empty body for ${response.url}`);
                return null; // Or decide to throw based on API contract
            }
            return null; // Empty body is fine if expectNoContent could be true (e.g., DELETE might sometimes return 200 OK with empty body)
        }
        
        try {
            const jsonData = JSON.parse(text); // Parse the text we already read

            // --- BEGIN MODIFICATION: Check for login_required status in successful response ---
            if (jsonData && jsonData.status === 'login_required') {
                console.warn(`[API] Received 'login_required' status in response for ${response.url}. Triggering auth failure event.`);
                window.dispatchEvent(new CustomEvent('auth-failure', { detail: { endpoint: response.url, reason: 'status_login_required' } }));
                throw new Error('操作需要登录，请先登录。'); // Throw specific error
            }
            // --- END MODIFICATION ---

            return jsonData;
        } catch(e) {
            // Check if the error was the specific one we threw above
            if (e.message.startsWith('操作需要登录')) {
                 throw e;
            }
            console.error(`[API] Failed to parse JSON response for ${response.url}:`, e, "Response text:", text);
            throw new Error('无法解析服务器响应。');
        }
    } else {
        // Handle non-OK responses (4xx, 5xx, excluding 401 which is handled in _fetchWithAuth)
        let errorMsg = `请求失败 (${response.status})`;
        try {
            // Try to parse error details from body
            const errorText = await response.text(); // Read error body as text first
            if (errorText) {
                const errorData = JSON.parse(errorText);
                // Check for specific status first, then standard error/message
                if (errorData.status === 'login_required') { // Handle login_required in error responses too
                     console.warn(`[API] Received 'login_required' status in error response for ${response.url}. Triggering auth failure event.`);
                     window.dispatchEvent(new CustomEvent('auth-failure', { detail: { endpoint: response.url, reason: 'status_login_required_error' } }));
                     throw new Error('操作需要登录，请先登录。');
                }
                errorMsg = `${errorMsg}: ${errorData.error || errorData.message || errorText || '未知后端错误'}`;
            } else {
                 errorMsg = `${errorMsg}: 无错误详情`;
            }
        } catch (e) {
            // Ignore if error response is not JSON or other parsing error
             errorMsg = `${errorMsg}: 无法解析错误详情`; 
        }
        console.error(`[API] Request failed for ${response.url}: ${errorMsg}`);
        throw new Error(errorMsg);
    }
}

// --- Public API Functions ---

/**
 * Fetches all subscriptions for the logged-in user.
 * @returns {Promise<Array<object>>} Array of subscription objects.
 */
export async function getSubscriptions() {
    console.log('[API] Getting subscriptions...');
    const response = await _fetchWithAuth('/api/subscriptions', { method: 'GET' });
    const data = await _handleApiResponse(response);
    return data.subscriptions || []; // Ensure returning an array
}

/**
 * Creates a new subscription.
 * @param {object} subscriptionData - Data for the new subscription.
 * @returns {Promise<object>} The newly created subscription object returned by the server.
 */
export async function createSubscription(subscriptionData) {
     console.log('[API] Creating subscription...', subscriptionData);
     const response = await _fetchWithAuth('/api/subscriptions', {
         method: 'POST',
         body: subscriptionData,
     });
     return await _handleApiResponse(response);
}

/**
 * Updates an existing subscription.
 * @param {string} id - The ID of the subscription to update.
 * @param {object} updateData - An object containing fields to update (e.g., { reminder: true }).
 * @returns {Promise<object>} The updated subscription object.
 */
export async function updateSubscription(id, updateData) {
    console.log(`[API] Updating subscription ${id}...`, updateData);
    const response = await _fetchWithAuth(`/api/subscriptions/${id}`, {
        method: 'PUT',
        body: updateData,
    });
    return await _handleApiResponse(response);
}

/**
 * Deletes a subscription.
 * @param {string} id - The ID of the subscription to delete.
 * @returns {Promise<void>} Resolves when deletion is successful (handles 204 No Content).
 */
export async function deleteSubscription(id) {
    console.log(`[API] Deleting subscription ${id}...`);
    const response = await _fetchWithAuth(`/api/subscriptions/${id}`, {
        method: 'DELETE'
    });
    await _handleApiResponse(response, true); // Expect 204 No Content
}

/**
 * Sends an image to the backend for parsing.
 * @param {FormData} formData - FormData object containing the image file under the key 'image'.
 * @returns {Promise<object>} The structured response from the backend 
 *                            { status: 'complete'/'incomplete'/'awaiting_confirmation', data?, conversationId?, question?, missingFields? }.
 * @throws {Error} Throws error for network, API failures, or login required.
 */
export async function parseImage(formData) {
    console.log('[API] Sending image for parsing...');
    // --- MODIFICATION: Require authentication for parsing --- 
    const response = await _fetchWithAuth('/api/subscriptions/parse-image', {
        method: 'POST',
        body: formData // Send FormData directly
    }, true); // Requires Auth: true

    // Return the full structured response from the backend
    const result = await _handleApiResponse(response);
    console.log('[API] parseImage received result:', result);
    // --- MODIFICATION: Validate structure to include awaiting_confirmation ---
    if (!result || !result.status || !['complete', 'incomplete', 'awaiting_confirmation', 'error'].includes(result.status)) {
        console.error('[API] Unexpected response status from parseImage:', result ? result.status : 'undefined');
        throw new Error('从服务器收到了意外的响应格式或状态。');
    }
    // --- END MODIFICATION ---
    return result; // Return the whole object { status, data?, conversationId?, question?, ... }
}

/**
 * Sends text to the backend for parsing.
 * @param {string} text - The text input.
 * @returns {Promise<object>} The structured response from the backend 
 *                            { status: 'complete'/'incomplete'/'awaiting_confirmation', data?, conversationId?, question?, missingFields? }.
 * @throws {Error} Throws error for network, API failures, or login required.
 */
export async function parseText(text) {
    console.log('[API] Sending text for parsing...');
     // --- MODIFICATION: Require authentication for parsing --- 
    const response = await _fetchWithAuth('/api/subscriptions/parse', {
        method: 'POST',
        body: { text }
    }, true); // Requires Auth: true

    // Return the full structured response from the backend
    const result = await _handleApiResponse(response);
    console.log('[API] parseText received result:', result);
    // --- MODIFICATION: Validate structure to include awaiting_confirmation ---
    if (!result || !result.status || !['complete', 'incomplete', 'awaiting_confirmation', 'error'].includes(result.status)) {
         console.error('[API] Unexpected response status from parseText:', result ? result.status : 'undefined');
         throw new Error('从服务器收到了意外的响应格式或状态。');
    }
    // --- END MODIFICATION ---
    return result; // Return the whole object
}

/**
 * Sends a follow-up message to continue an ongoing parsing conversation.
 * @param {string} conversationId The ID of the conversation to continue.
 * @param {string} userReply The user's reply text.
 * @returns {Promise<object>} The structured response from the backend 
 *                            { status: 'complete'/'incomplete'/'awaiting_confirmation', data?, conversationId?, question?, missingFields? }.
 * @throws {Error} Throws error for network, API failures, or login required.
 */
export async function continueParse(conversationId, userReply) {
    console.log(`[API] Sending follow-up for conversation ${conversationId}...`);
     // --- MODIFICATION: Require authentication for parsing --- 
    const response = await _fetchWithAuth('/api/subscriptions/continue-parse', {
        method: 'POST',
        body: { conversationId, userReply }
    }, true); // Requires Auth: true

    // Return the full structured response from the backend
    const result = await _handleApiResponse(response);
    console.log(`[API] continueParse received result for ${conversationId}:`, result);
     // --- MODIFICATION: Validate structure to include awaiting_confirmation ---
     if (!result || !result.status || !['complete', 'incomplete', 'awaiting_confirmation', 'error'].includes(result.status)) {
         console.error('[API] Unexpected response status from continueParse:', result ? result.status : 'undefined');
         throw new Error('从服务器收到了意外的响应格式或状态。');
     }
     // --- END MODIFICATION ---
    return result;
}

/**
 * Synchronizes the local subscription list with the backend.
 * Sends the client's list; the backend handles merging and returns the definitive list.
 * @param {Array<object>} subscriptionsToSync - The array of subscription objects currently stored locally.
 * @returns {Promise<Array<object>>} The synchronized array of subscription objects from the server.
 * @throws {Error} Throws error for network, API failures, or login required.
 */
export async function syncSubscriptions(subscriptionsToSync) {
     console.log(`[API] Syncing ${subscriptionsToSync.length} subscriptions...`);
     if (!Array.isArray(subscriptionsToSync)) {
         console.error('[API] syncSubscriptions requires an array.');
         throw new Error('同步数据格式错误：需要一个数组。');
     }
     const response = await _fetchWithAuth('/api/sync', {
         method: 'POST',
         body: { subscriptions: subscriptionsToSync }, // Send wrapped in { subscriptions: [...] }
     }, true); // Requires Auth: true
 
     const result = await _handleApiResponse(response);
     console.log(`[API] Sync completed. Received ${result.subscriptions?.length ?? 0} subscriptions.`);
     
     // Validate the response structure
     if (!result || !Array.isArray(result.subscriptions)) {
         console.error('[API] Unexpected response structure from syncSubscriptions:', result);
         throw new Error('从服务器收到的同步响应格式无效。');
     }
     
     return result.subscriptions; // Return just the array
 }
